import { versionKey } from './video-library-api';

/**
 * Pure helpers over an ad entity's version history (`ad_versions`, banner kits and mini-games). The rule is the
 * video one: a version LOCKS once generated, and editing a locked version first forks a new unlocked one, so a
 * generated result is never mutated in place. A version's identity is its `created_at`, compared through
 * {@link versionKey} (epoch-ms text), because the database round-trip may reformat the timestamp.
 */

export { versionKey };

/** The minimal shape every ad version shares. */
export interface AdVersionLike {
  created_at: string;
  locked?: boolean;
}

const timeOf = (v: AdVersionLike): number => {
  const ms = Date.parse(v.created_at);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};

/** The version whose key is `key` (a {@link versionKey} or a raw `created_at`), else undefined. */
export const findVersion = <V extends AdVersionLike>(versions: readonly V[], key: string | undefined): V | undefined => {
  if (!key) return undefined;
  const wanted = versionKey(key);
  return versions.find((v) => versionKey(v.created_at) === wanted);
};

/** The newest version by `created_at` (the backend keeps them newest first; this does not rely on it). */
export const newestVersion = <V extends AdVersionLike>(versions: readonly V[]): V | undefined =>
  versions.reduce<V | undefined>((best, v) => (!best || timeOf(v) > timeOf(best) ? v : best), undefined);

/** The newest LOCKED (generated) version, the one a library card downloads. */
export const newestLockedVersion = <V extends AdVersionLike>(versions: readonly V[]): V | undefined =>
  newestVersion(versions.filter(isLocked));

/** Whether a version is locked (generated). An absent version is not. */
export const isLocked = (v: AdVersionLike | undefined): boolean => v?.locked === true;

/** The entry that forks a version into a new unlocked one: a deep copy of everything it carries (params, hero,
 *  shape renders, assets) without its identity (`created_at`) or lock, ready for an upsert's `ad_version`. */
export const forkEntry = <V extends AdVersionLike>(v: V): Omit<V, 'created_at' | 'locked'> => {
  const { created_at: _createdAt, locked: _locked, ...rest } = v;
  // A deep copy, so editing the fork (the optimistic cache) never mutates the locked original.
  return JSON.parse(JSON.stringify(rest)) as Omit<V, 'created_at' | 'locked'>;
};

type Versioned = { ad_versions: ReadonlyArray<{ created_at: string; locked?: boolean; params?: unknown }> };

const isVersioned = (v: unknown): v is Versioned =>
  typeof v === 'object' && v !== null && Array.isArray((v as { ad_versions?: unknown }).ad_versions);

/**
 * A refetched list with the editor's not-yet-saved versions kept: a fork is added to the cached entity at once and
 * written to the server a moment later, so a list read that answers in between would drop it (the editor then loses
 * its selection and forks again). A cached version the server does not have and that is newer than every version
 * the server returned is kept on top. `serverWins` names a row whose server document wins over the editor's (see
 * below). Pure.
 */
export const keepPendingVersions = <T extends { id: string }>(
  prev: readonly T[],
  rows: T[],
  serverWins?: (row: T) => boolean,
): T[] =>
  rows.map((row) => {
    const local = prev.find((p) => p.id === row.id);
    if (!local || !isVersioned(local) || !isVersioned(row)) return row;
    const known = new Set(row.ad_versions.map((v) => versionKey(v.created_at)));
    const newest = Math.max(-Infinity, ...row.ad_versions.map((v) => Date.parse(v.created_at)));
    const pending = local.ad_versions.filter(
      (v) => !known.has(versionKey(v.created_at)) && Date.parse(v.created_at) > newest,
    );
    // A draft's document is edited in the editor first and saved on the draft a moment later (the debounced save):
    // a read that answers in between keeps the editor's document on a draft both sides still have unlocked. Except on
    // the read that follows the entity's own run (`serverWins`): the run wrote the draft on the server (a copy-only
    // run leaves it unlocked) after the editor's last save, so the server's document is the newer one.
    const fresh = serverWins?.(row) ?? false;
    const mine = new Map(local.ad_versions.map((v) => [versionKey(v.created_at), v]));
    let changed = false;
    const versions = row.ad_versions.map((v) => {
      const own = mine.get(versionKey(v.created_at));
      if (fresh || !own || isLocked(own) || isLocked(v) || own.params === undefined || own.params === v.params) return v;
      changed = true;
      return { ...v, params: own.params };
    });
    if (pending.length === 0 && !changed) return row;
    return { ...row, ad_versions: [...pending, ...versions] };
  });

/**
 * A freshly created entity as the editor should see it: the listed row once the list has it, with the versions the
 * editor added to its own copy (a first fork, kept on the created snapshot because the list did not have the entity
 * yet) that the list does not have yet; the created snapshot until then. Pure.
 */
export const withCreated = <T extends { id: string }>(listed: readonly T[], created: T): T => {
  const row = listed.find((x) => x.id === created.id);
  return row ? keepPendingVersions([created], [row])[0] : created;
};
